import * as React from "react";
import { formInitialize, formUninitialize, useCpFormAnyValueId, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { useBlocker } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { getValidator } from "./validator";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { asyncInputToCloudAccount, getDefaultCloudAccountInput, inputToCloudAccountPartial } from "../utils";
import { CloudAccountInput } from "../../../schema/types/cloudaccount/input";
import { CloudAccountCreateContent } from "./content";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { cloudAccountApi } from "../../../store/api/cloudaccountApi";

interface Props {}
export const CloudAccountCreateForm: React.FC<Props> = () => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultCloudAccountInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  const provider = useCpFormAnyValueId(formId, "values.provider");

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/cloudaccount/-create`) &&
      !nextLocation.pathname.includes(`/cloudaccount/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: CloudAccountInput) {
    let secretLink = "";
    try {
      const { body, secretLink: createdSecretLink } = await asyncInputToCloudAccount(input);
      secretLink = createdSecretLink;
      const method = "post";
      const url = homeLink("cloudaccount");

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("cloudaccount", body.name);
      dispatch(cloudAccountApi.util.invalidateTags([{ type: "CloudAccount", id: itemLink }]));
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "Cloud Account is created",
      });
      if (input.provider === "gcp") {
        dispatch(setRedirect(`/console${itemLink}/-configuration`));
      } else {
        dispatch(setRedirect(`/console${itemLink}`));
      }
    } catch (e) {
      try {
        if (secretLink) {
          await request({ method: "delete", url: secretLink });
        }
      } catch (e) {
        let secretCleanUpErrorMessage = e?.response?.data?.message;
        if (!secretCleanUpErrorMessage) secretCleanUpErrorMessage = e.message;
        notification.warning({
          message: "Failed to cleanup secret",
          description: secretCleanUpErrorMessage,
        });
      }
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <CloudAccountCreateContent ignoreBlockerRef={ignoreBlockerRef} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal
        inputToItemFn={inputToCloudAccountPartial}
        kind="cloudaccount"
        namePath="name"
        title="Cloud Account"
      />
      <CpDebugModal
        inputToItemFn={inputToCloudAccountPartial}
        kind="cloudaccount"
        namePath="name"
        title="Cloud Account"
      />
    </CpFormContext.Provider>
  );
};
